/**
 * Compresses an image file to reduce its size while maintaining quality
 * @param {File} file - The original image file
 * @param {Object} options - Compression options
 * @returns {Promise<File>} - The compressed image file
 */
export const compressImage = (file, options = {}) => {
  return new Promise((resolve, reject) => {
    const {
      maxWidth = 800,
      maxHeight = 800,
      quality = 0.8,
      outputFormat = 'image/jpeg'
    } = options;

    // Check if Canvas API is available
    if (typeof window === 'undefined' || !window.HTMLCanvasElement) {
      console.warn('Canvas API not available, returning original file');
      resolve(file);
      return;
    }

    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');
    
    if (!ctx) {
      console.warn('Canvas 2D context not available, returning original file');
      resolve(file);
      return;
    }

    const img = new Image();

    img.onload = () => {
      try {
        // Calculate new dimensions while maintaining aspect ratio
        let { width, height } = img;
        
        if (width > height) {
          if (width > maxWidth) {
            height = (height * maxWidth) / width;
            width = maxWidth;
          }
        } else {
          if (height > maxHeight) {
            width = (width * maxHeight) / height;
            height = maxHeight;
          }
        }

        // Set canvas dimensions
        canvas.width = width;
        canvas.height = height;

        // Draw and compress image
        ctx.drawImage(img, 0, 0, width, height);

        // Convert to blob with better error handling
        canvas.toBlob(
          (blob) => {
            if (blob) {
              // Create new file with compressed data
              const compressedFile = new File(
                [blob],
                file.name.replace(/\.[^/.]+$/, '') + '.jpg', // Always convert to JPG for consistency
                {
                  type: outputFormat,
                  lastModified: Date.now()
                }
              );
              
              console.log(`📊 Image compression: ${(file.size / 1024).toFixed(1)}KB → ${(compressedFile.size / 1024).toFixed(1)}KB`);
              resolve(compressedFile);
            } else {
              console.warn('Canvas toBlob failed, returning original file');
              resolve(file);
            }
          },
          outputFormat,
          quality
        );
      } catch (error) {
        console.warn('Image compression failed:', error);
        resolve(file); // Return original file instead of rejecting
      }
    };

    img.onerror = () => {
      console.warn('Failed to load image for compression, returning original file');
      resolve(file); // Return original file instead of rejecting
    };

    img.src = URL.createObjectURL(file);
  });
};

/**
 * Creates a thumbnail version of an image for avatar display
 * @param {File} file - The original image file
 * @returns {Promise<File>} - The thumbnail image file
 */
export const createThumbnail = (file) => {
  return compressImage(file, {
    maxWidth: 150,
    maxHeight: 150,
    quality: 0.85,
    outputFormat: 'image/jpeg'
  });
};

/**
 * Validates image file before upload
 * @param {File} file - The image file to validate
 * @returns {Object} - Validation result with isValid and error message
 */
export const validateImageFile = (file) => {
  const MAX_FILE_SIZE = 10 * 1024 * 1024; // 10MB
  const ALLOWED_TYPES = ['image/jpeg', 'image/jpg', 'image/png', 'image/gif', 'image/webp', 'image/heic', 'image/heif'];

  if (!file) {
    return { isValid: false, error: 'No file provided' };
  }

  if (!ALLOWED_TYPES.includes(file.type)) {
    return { isValid: false, error: 'Invalid file type. Please use JPG, PNG, GIF, WebP, or HEIC format.' };
  }

  if (file.size > MAX_FILE_SIZE) {
    return { isValid: false, error: 'File size too large. Maximum size is 10MB.' };
  }

  return { isValid: true, error: null };
};

/**
 * Gets the optimal image size based on display context
 * @param {string} context - The context where the image will be displayed
 * @returns {Object} - Width and height for the image
 */
export const getOptimalImageSize = (context) => {
  const sizes = {
    avatar_small: { width: 32, height: 32 },
    avatar_medium: { width: 64, height: 64 },
    avatar_large: { width: 150, height: 150 },
    profile_photo: { width: 300, height: 300 }
  };

  return sizes[context] || sizes.avatar_medium;
};

/**
 * Preloads an image to improve perceived performance
 * @param {string} src - Image source URL
 * @returns {Promise} - Resolves when image is loaded
 */
export const preloadImage = (src) => {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => resolve(img);
    img.onerror = reject;
    img.src = src;
  });
};

/**
 * Creates a cache key for image caching
 * @param {string} url - Image URL
 * @param {number} size - Image size
 * @returns {string} - Cache key
 */
export const createImageCacheKey = (url, size) => {
  return `img_${btoa(url)}_${size}`;
};

/**
 * Simple in-memory image cache
 */
class ImageCache {
  constructor(maxSize = 50) {
    this.cache = new Map();
    this.maxSize = maxSize;
  }

  get(key) {
    const item = this.cache.get(key);
    if (item) {
      // Move to end (most recently used)
      this.cache.delete(key);
      this.cache.set(key, item);
      return item;
    }
    return null;
  }

  set(key, value) {
    // Remove oldest items if cache is full
    if (this.cache.size >= this.maxSize) {
      const firstKey = this.cache.keys().next().value;
      this.cache.delete(firstKey);
    }
    
    this.cache.set(key, value);
  }

  clear() {
    this.cache.clear();
  }
}

export const imageCache = new ImageCache(); 